import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import type { gridSpans } from "@patternfly/react-core";
import { Grid, GridItem } from "@patternfly/react-core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FC } from "react";
import { getDashboardPreferences, saveDashboardPreferences } from "../../api/pfs";
import { findImagesCountByOrgId } from "../../api/pyxis";
import type { IPartnershipStatus } from "../../constants/apiconstants-pfs";
import type { ITotalImagesCount } from "../../constants/apiconstants-pyxis";
import { CardRegistry, CardTypeCode, type ICardRegistryEntry } from "../../constants/CardsStructure";
import { usePartnerInfo } from "../../queries/partnerInfo";
import { getOrgID } from "../../utils/fetch";

const getColSpan = (code: CardTypeCode): { md: number; lg: number } => {
	const HalfWidthCards = new Set([
		CardTypeCode.SalesBusinessPartners,
		CardTypeCode.LowGradeImages,
		CardTypeCode.PartnerTraining,
		CardTypeCode.Webinars,
		CardTypeCode.SupportCard,
		CardTypeCode.OtherResources,
		CardTypeCode.ManageMyCompany,
		CardTypeCode.MyCompany,
	]);
	const ThirdWidthCards = new Set([CardTypeCode.HardwarePartners, CardTypeCode.CloudPartners]);
	const QuarterWidthCards = new Set([
		CardTypeCode.ContentCenter,
		CardTypeCode.DemoPlatform,
		CardTypeCode.EmailPreferences,
		CardTypeCode.UserAccount,
		CardTypeCode.PartnerSubscriptions,
	]);

	if (HalfWidthCards.has(code)) return { md: 6, lg: 6 };
	if (ThirdWidthCards.has(code)) return { md: 6, lg: 4 };
	if (QuarterWidthCards.has(code)) return { md: 6, lg: 3 };
	return { md: 12, lg: 12 };
};

const SortableCardItem: FC<{ entry: ICardRegistryEntry }> = ({ entry }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: entry.cardTypeCode,
	});
	const { md, lg } = getColSpan(entry.cardTypeCode);

	return (
		<GridItem sm={12} md={md as gridSpans} lg={lg as gridSpans}>
			<div
				ref={setNodeRef}
				style={{
					transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
					transition,
					opacity: isDragging ? 0.5 : 1,
					height: "100%",
				}}
			>
				{entry.renderCard({ attributes, listeners: listeners ?? {} })}
			</div>
		</GridItem>
	);
};

const DraggableDashboard: FC = () => {
	const queryClient = useQueryClient();
	const partnershipStatus = queryClient.getQueryState(["partnershipStatus"])?.data as IPartnershipStatus;

	const { data: partnerInfo, isFetching: partnerInfoLoading } = usePartnerInfo({
		enabled: !!partnershipStatus?.isPartner,
	});

	const { data: preferences, isPending: prefsLoading } = useQuery({
		queryKey: ["dashboardPreferences"],
		queryFn: ({ signal }) => getDashboardPreferences(signal),
	});

	const orgId = getOrgID();
	const { data: totalImageCount } = useQuery<ITotalImagesCount>({
		queryKey: ["totalImageCount"],
		queryFn: () => findImagesCountByOrgId({ page: 1, page_size: 1 }, { org_id: { eq: parseInt(orgId, 10) } }),
		enabled: !!partnershipStatus?.isPartner && !!partnershipStatus?.buildPartner,
	});

	const visibleCards = CardRegistry.filter((entry) =>
		entry.isVisible(partnershipStatus, partnerInfo, partnerInfoLoading, totalImageCount),
	);

	const orderedCards = (() => {
		if (!preferences?.cards?.length) return visibleCards;
		const savedCodes = preferences.cards.map((c) => c.cardTypeCode);
		return visibleCards
			.filter((e) => savedCodes.includes(e.cardTypeCode))
			.sort((a, b) => savedCodes.indexOf(a.cardTypeCode) - savedCodes.indexOf(b.cardTypeCode));
	})();

	const { data: cards } = useQuery<ICardRegistryEntry[] | null>({
		queryKey: ["cardOrder"],
		queryFn: () => null,
		initialData: null,
		staleTime: Infinity,
		gcTime: Infinity,
		enabled: false,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});
	const displayCards = cards ?? orderedCards;

	const visibleCardCodes = new Set(visibleCards.map((e) => e.cardTypeCode));
	const invisibleCards = CardRegistry.filter((e) => !visibleCardCodes.has(e.cardTypeCode));

	const saveMutation = useMutation({
		mutationFn: (newCards: { cardTypeCode: string }[]) => saveDashboardPreferences(newCards),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["dashboardPreferences"] }),
	});

	const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			const oldIndex = displayCards.findIndex((c) => c.cardTypeCode === active.id);
			const newIndex = displayCards.findIndex((c) => c.cardTypeCode === over.id);
			const reordered = arrayMove(displayCards, oldIndex, newIndex);
			queryClient.setQueryData(["cardOrder"], reordered);
			const allCards = [
				...reordered.map((c) => ({ cardTypeCode: c.cardTypeCode })),
				...invisibleCards.map((c) => ({ cardTypeCode: c.cardTypeCode })),
			];
			saveMutation.mutate(allCards);
		}
	};

	if (prefsLoading) return null;

	return (
		<div className="authenticated-dashboard">
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
				<SortableContext items={displayCards.map((c) => c.cardTypeCode)} strategy={rectSortingStrategy}>
					<Grid hasGutter>
						{displayCards.map((entry) => (
							<SortableCardItem key={entry.cardTypeCode} entry={entry} />
						))}
					</Grid>
				</SortableContext>
			</DndContext>
		</div>
	);
};

export default DraggableDashboard;
